import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal } from 'react-native';
import { Notification } from '../store/NotificationStore';
import { SkiaCanvas } from './SkiaCanvas';
import { InputStroke } from '@eduwrite/shared';

interface NotificationDetailProps {
    notification: Notification;
    onClose: () => void;
}

export const NotificationDetail: React.FC<NotificationDetailProps> = ({ notification, onClose }) => {
    const originalStrokes = notification.payload?.originalStrokes as InputStroke[] | undefined;
    const overlayStrokes = notification.payload?.overlay as InputStroke[] | undefined;
    const targetText = notification.payload?.targetText;

    // Only show the canvas when there is something task-related to display.
    // Task-free feedback (general comment from teacher, no activity selected) has
    // no strokes or overlay — rendering SkiaCanvas with null data crashes.
    const hasTaskData = !!(
        targetText ||
        (overlayStrokes && overlayStrokes.length > 0) ||
        (originalStrokes && originalStrokes.length > 0)
    );

    return (
        <Modal
            visible={true}
            animationType="slide"
            presentationStyle="pageSheet"
            onRequestClose={onClose}
        >
            <View style={styles.container} accessibilityLabel="Teacher feedback detail">
                <View style={styles.header}>
                    <View>
                        <Text style={styles.title} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Teacher Feedback</Text>
                        {targetText && (
                            <Text style={styles.subtitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Activity: {String(targetText)}</Text>
                        )}
                    </View>
                    <TouchableOpacity onPress={onClose} style={styles.closeButton} accessibilityRole="button" accessibilityLabel="Done" accessibilityHint="Double tap to close the feedback detail">
                        <Text style={styles.closeText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Done</Text>
                    </TouchableOpacity>
                </View>

                {hasTaskData ? (
                    <View style={styles.canvasContainer}>
                        <SkiaCanvas
                            readOnly={true}
                            strokes={originalStrokes}
                            overlay={overlayStrokes}
                        />

                        {(!originalStrokes || originalStrokes.length === 0) && (
                            <View style={styles.warningContainer}>
                                <Text style={styles.warningText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                    Original student work not available for this session.
                                </Text>
                            </View>
                        )}
                    </View>
                ) : (
                    <View style={styles.noTaskContainer}>
                        <Text style={styles.noTaskIcon} allowFontScaling={true} maxFontSizeMultiplier={1.5}>👩‍🏫</Text>
                        <Text style={styles.noTaskText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>General feedback from your teacher</Text>
                    </View>
                )}

                <View style={styles.footer}>
                    <View style={styles.feedbackCard}>
                        <Text style={styles.feedbackLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Feedback:</Text>
                        <Text style={styles.feedbackText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{String(notification.message || 'Feedback')}</Text>
                        <Text style={styles.timestamp} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                            {notification.timestamp ? new Date(notification.timestamp).toLocaleString() : ''}
                        </Text>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F8FAFC',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 20,
        backgroundColor: '#FFFFFF',
        borderBottomWidth: 1,
        borderBottomColor: '#E2E8F0',
    },
    title: {
        fontSize: 20,
        fontWeight: 'bold',
        color: '#1E293B',
    },
    subtitle: {
        fontSize: 14,
        color: '#64748B',
        marginTop: 2,
    },
    closeButton: {
        backgroundColor: '#3B82F6',
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderRadius: 20,
    },
    closeText: {
        color: '#FFFFFF',
        fontWeight: '600',
    },
    canvasContainer: {
        flex: 1,
        backgroundColor: '#FFFFFF',
        margin: 16,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        overflow: 'hidden',
        position: 'relative',
    },
    warningContainer: {
        position: 'absolute',
        top: 10,
        left: 0,
        right: 0,
        alignItems: 'center',
    },
    warningText: {
        backgroundColor: 'rgba(255, 200, 0, 0.8)',
        padding: 8,
        borderRadius: 8,
        fontSize: 12,
        color: '#000',
    },
    footer: {
        padding: 20,
        backgroundColor: '#FFFFFF',
        borderTopWidth: 1,
        borderTopColor: '#E2E8F0',
    },
    feedbackCard: {
        backgroundColor: '#F0F9FF',
        padding: 16,
        borderRadius: 12,
        borderLeftWidth: 4,
        borderLeftColor: '#3B82F6',
    },
    feedbackLabel: {
        fontSize: 12,
        fontWeight: '700',
        color: '#3B82F6',
        marginBottom: 4,
        textTransform: 'uppercase',
    },
    feedbackText: {
        fontSize: 16,
        color: '#1E293B',
        lineHeight: 24,
    },
    timestamp: {
        marginTop: 8,
        fontSize: 11,
        color: '#94A3B8',
        textAlign: 'right',
    },
    noTaskContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 40,
    },
    noTaskIcon: {
        fontSize: 64,
        marginBottom: 16,
    },
    noTaskText: {
        fontSize: 18,
        color: '#64748B',
        textAlign: 'center',
    },
});
